import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/modules/auth'
import { createApiClient } from '@/shared/api/client'
import { ApiError } from '@/shared/api/types'

export type GradeCategory = 'easy' | 'moderate' | 'steep' | 'extreme'

export interface RouteGeometryWaypoint {
  id: string
  name: string
  latitude: number
  longitude: number
  orderIndex: number
  tripDay: number | null
}

export interface RouteGeometryPoi {
  id: string
  name: string
  coordinates: [number, number]
  poiType: string | null
  tripDay: number | null
}

export interface ElevationSample {
  distanceMeters: number
  elevationMeters: number
  /** Index into the route's coordinates array */
  coordIdx: number
}

export interface GradeSegment {
  startIdx: number
  endIdx: number
  grade: number
  category: GradeCategory
}

export interface RouteGeometryData {
  coordinates: [number, number][]
  distanceMeters: number
  durationSeconds: number
  waypoints: RouteGeometryWaypoint[]
  pois: RouteGeometryPoi[]
  elevationProfile: ElevationSample[]
  gradeSegments: GradeSegment[]
  previewOnly: boolean
}

export interface UseRouteGeometryResult {
  data: RouteGeometryData | null
  isLoading: boolean
}

interface RouteGeometryApiResponse {
  success: boolean
  data: {
    geometry: { type: 'LineString'; coordinates: [number, number][] }
    distanceMeters: number
    durationSeconds: number
    waypoints: RouteGeometryWaypoint[]
    pois?: RouteGeometryPoi[]
    elevationProfile?: ElevationSample[]
    gradeSegments?: GradeSegment[]
    previewOnly: boolean
  }
}

export function useRouteGeometry(tripId: string, enabled = true): UseRouteGeometryResult {
  const { getAccessToken } = useAuth()
  const api = useMemo(() => createApiClient(getAccessToken), [getAccessToken])

  const { data, isLoading } = useQuery<RouteGeometryData, ApiError>({
    queryKey: ['route-geometry', tripId],
    queryFn: async () => {
      const response = await api.get<RouteGeometryApiResponse>(`/trips/${tripId}/route-geometry`)
      return {
        coordinates: response.data.geometry.coordinates,
        distanceMeters: response.data.distanceMeters,
        durationSeconds: response.data.durationSeconds,
        waypoints: response.data.waypoints,
        pois: response.data.pois ?? [],
        elevationProfile: response.data.elevationProfile ?? [],
        gradeSegments: response.data.gradeSegments ?? [],
        previewOnly: response.data.previewOnly ?? false,
      }
    },
    enabled: !!tripId && enabled,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 403 || err.status === 400)) return false
      return failureCount < 2
    },
  })

  return {
    data: data ?? null,
    isLoading,
  }
}
